// Public client REPORT acknowledgement page (no login — token-scoped). Renders the formal ReportDocument
// (the actual audit report) for the client to review, then captures a formal acknowledgement bound to the
// current report version. Immutable; the server records timestamp, IP, user-agent, version and access email.
//
// It ALSO collects the WSHC online-application details (retires the separate Excel) — bundled with the
// endorsement so it can't be forgotten. Known values are pre-filled (Employees = auditor-verified); the
// client completes the rest. Saved on the job for the office (never shown on the audit report).
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import ReportDocument from '../components/ReportDocument'
import { rasterisePdf, buildAppendixList, appendixPrintHtml, resolveSnapshotMedia } from '../lib/reportAppendix'
import { emailHtml } from '../lib/emailHtml'
import { toast, failed } from '../lib/toast'

const DECLARATION = 'I confirm that I have reviewed this audit report and acknowledge its contents.'

// New WSHC client-portal process (bizSAFE jobs with wshc.flow = 'client_portal'): no application form — the
// client applies on WSHC's portal themselves. The earlier process keeps this page exactly as before.
const WSHC_PORTAL_URL = 'https://bizsafe.wshc.sg/'
const WSHC_GUIDE_URL = 'https://www.tal.sg/wshc/-/media/tal/wshc/programmes/files/user-guide-for-bizsafe-portal.pdf'
const PORTAL_LEAD = 'From 2 November 2026, companies must apply for bizSAFE themselves on WSHC’s new portal. CCIS can no longer apply for you.'
const fmtDate = (d) => d ? new Date(String(d).length === 10 ? d + 'T00:00:00' : d).toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Singapore' }) : ''
const todaySg = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Singapore' })
const validEmail = (e) => /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/.test(String(e || '').trim())

function ApplySteps() {
  return (
    <div style={{ marginTop: 8, lineHeight: 1.7 }}>
      <div style={{ fontWeight: 600 }}>Your next steps</div>
      <div><strong>Step 1:</strong> Download the signed report (button below).</div>
      <div><strong>Step 2:</strong> Go to <a href={WSHC_PORTAL_URL} target="_blank" rel="noreferrer">bizsafe.wshc.sg</a> &gt; “bizSAFE Programmes” &gt; “Login to Apply”.</div>
      <div><strong>Step 3:</strong> Log in with your company’s CorpPass. (No access? Ask your CorpPass admin to add the bizSAFE e-service.)</div>
      <div><strong>Step 4:</strong> Upload the report and click Submit.</div>
      <div style={{ marginTop: 4 }}><strong>Stuck?</strong> See the <a href={WSHC_GUIDE_URL} target="_blank" rel="noreferrer"><strong>WSHC user guide</strong></a> or call us at (65) 6376 0550.</div>
    </div>
  )
}
const wrap = { minHeight: '100vh', background: '#f7f6f3', padding: '24px 16px' }
const sheet = { background: '#fff', borderRadius: 12, padding: 'clamp(16px, 4vw, 30px)', maxWidth: 880, margin: '0 auto', border: '0.5px solid rgba(0,0,0,0.12)' }
const lockStyle = { background: '#f3f3f1', color: '#555' }

const CERT_OPTIONS = [
  'SCAL SLOTS Membership Certificate and SgMA Certificate',
  'Responsible Care Award Winner',
  'Joint Commission International (JCI)',
  'ISO 45001',
  'Singapore Standard (SS) 506',
  'None of the above',
]
const NONE_CERT = 'None of the above'
const INDUSTRY_SECTORS = ['Chemicals', 'Construction', 'Engineering', 'Event/Entertainment Organisers', 'Facilities Management', 'Financial and Insurance Activities', 'Food & Beverages', 'Government & Statutory Boards', 'Healthcare', 'Hotels/Accommodation for Commercial Gains', 'Landscape Care & Maintenance Activities', 'Logistics & Transportation', 'Manufacturing (Metalworking)', 'Manufacturing (Others)', 'Marine', 'Oil & Gas', 'Others', 'Veterinary', 'Water Supply, Sewage & Waste Management Activities', 'Wholesale & Retail Trade']
const LEVELS = ['Level 3', 'Level 4', 'Level STAR']
const COMPANY_TYPES = ['Local', 'Overseas']
const DESIGNATIONS = ['CEO', 'Director/Managing Director', 'General Manager', 'Owner', 'Others']
const blankContact = () => ({ name: '', designation: '', designation_other: '', contact_no: '', mobile: '', email: '' })
const contactComplete = (c) => !!(c && c.name?.trim() && c.designation && (c.designation !== 'Others' || c.designation_other?.trim()) && c.contact_no?.trim() && c.mobile?.trim() && c.email?.trim())

function ContactBlock({ title, note, value, onChange, onCopy, invalid, bare }) {
  const set = (k, v) => onChange({ ...value, [k]: v })
  const err = (bad) => invalid && bad ? { borderColor: '#E24B4A' } : {}
  return (
    <div style={{ marginTop: bare ? 0 : 16 }}>
      {!bare && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{title}</div>
          {onCopy && <button type="button" className="sm" onClick={onCopy}>Copy from Contact Person</button>}
        </div>
      )}
      {!bare && note && <div style={{ fontSize: 11.5, color: '#777', margin: '2px 0 8px' }}>{note}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10, marginTop: bare ? 0 : 8 }}>
        <div className="field"><label>Name *</label><input value={value.name || ''} onChange={e => set('name', e.target.value)} style={err(!value.name?.trim())} /></div>
        <div className="field"><label>Designation *</label>
          <select value={value.designation || ''} onChange={e => set('designation', e.target.value)} style={err(!value.designation)}>
            <option value="">Select…</option>{DESIGNATIONS.map(d => <option key={d} value={d}>{d}</option>)}
          </select></div>
        {value.designation === 'Others' && <div className="field" style={{ gridColumn: '1 / -1' }}><label>Designation (please specify) *</label><input value={value.designation_other || ''} onChange={e => set('designation_other', e.target.value)} style={err(!value.designation_other?.trim())} /></div>}
        <div className="field"><label>Contact Number *</label><input value={value.contact_no || ''} onChange={e => set('contact_no', e.target.value)} style={err(!value.contact_no?.trim())} /></div>
        <div className="field"><label>Mobile Number *</label><input value={value.mobile || ''} onChange={e => set('mobile', e.target.value)} style={err(!value.mobile?.trim())} /></div>
        <div className="field" style={{ gridColumn: '1 / -1' }}><label>Email Address *</label><input value={value.email || ''} onChange={e => set('email', e.target.value)} style={err(!value.email?.trim())} /></div>
      </div>
    </div>
  )
}

export default function ClientReportAck() {
  const { token } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [invalid, setInvalid] = useState(false)
  const [form, setForm] = useState({ confirm: false, name: '', designation: '' })
  const [app, setApp] = useState(null)
  const [showErrors, setShowErrors] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)   // "save details now, sign later" (admin fills, mgmt signs)
  const [draftSaved, setDraftSaved] = useState(false)
  const [media, setMedia] = useState({})
  const [appendices, setAppendices] = useState([])   // appended PDFs (impl doc, manual checklist/interview, evidence pack)
  const endorseRef = useRef(null)   // target for the "Sign / endorse ↓" jump button in the top banner
  const applicantRef = useRef(null) // new WSHC-portal process: the jump button lands on the "staff who will apply" box
  const [mediaReady, setMediaReady] = useState(false)   // gate print until inline media has resolved
  const [underRevision, setUnderRevision] = useState(false)  // report withdrawn/returned → block acknowledgement
  const [pkg, setPkg] = useState({ mode: 'legacy', url: null })  // stored official package: ready | preparing | error | legacy
  const reportRef = useRef(null)
  // New WSHC client-portal process
  const [applicant, setApplicant] = useState({ name: '', email: '' })
  const [applyPicks, setApplyPicks] = useState({})        // combined audit: job_id → ticked for "Already applied"
  const [applyBusy, setApplyBusy] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const askApplied = new URLSearchParams(window.location.search).get('applied') === '1'   // from the reminder emails

  function printReport() {
    const html = reportRef.current?.innerHTML
    if (!html) return
    const w = window.open('', '_blank', 'width=900,height=1000')
    if (!w) { toast('Please allow pop-ups to save the PDF', 'warning'); return }
    w.document.write(`<!doctype html><html><head><title>Audit Report</title><meta charset="utf-8"><style>@page{margin:14mm}*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;box-sizing:border-box}html,body{margin:0;padding:0;height:auto;overflow:visible;font-family:Arial,Helvetica,sans-serif;color:#1a1a1a}img{max-width:100%}.pkg-noprint{display:none!important}</style></head><body>${html}${appendixPrintHtml(appendices)}</body></html>`)
    w.document.close(); w.focus(); setTimeout(() => w.print(), 350)
  }

  useEffect(() => { load() }, [token]) // eslint-disable-line
  async function load() {
    setLoading(true)
    const { data: d, error } = await supabase.rpc('get_report_for_ack', { p_token: token })
    if (error || !d || !d.job) { setInvalid(true); setLoading(false); return }
    setData(d)
    // Withdrawn-for-revision check (server is the authority; this only drives the message + hides the form).
    try { const { data: ur } = await supabase.rpc('report_ack_under_revision', { p_token: token }); setUnderRevision(!!ur) } catch (e) {}
    // Seed the application form: known values pre-filled (locked ones authoritative from prefill), prior answers
    // restored if the report was revised and they're re-endorsing.
    const pf = d.prefill || {}
    const prior = d.bizsafe_application || {}
    setApplicant({ name: d.wshc?.applicant_name || '', email: d.wshc?.applicant_email || '' })
    setApplyPicks(Object.fromEntries((d.wshc?.group || []).filter(g => !g.submitted_at).map(g => [g.job_id, true])))
    setApp({
      uen: pf.uen || prior.uen || '',
      company_name: pf.company_name || prior.company_name || '',
      application_type: pf.application_type || prior.application_type || '',
      address: pf.address || prior.address || '',
      employees: (pf.employees ?? prior.employees ?? ''),
      company_type: prior.company_type || '',
      is_sme: prior.is_sme || '',
      industry_sector: prior.industry_sector || '',
      level: prior.level || '',
      enterprise_no: prior.enterprise_no || '',
      business_description: prior.business_description || pf.business_nature || '',
      company_email: prior.company_email || '',
      company_website: prior.company_website || '',
      key_search_words: prior.key_search_words || '',
      has_certs: prior.has_certs || [],
      subsidiary: !!prior.subsidiary,
      contact: prior.contact || { ...blankContact(), name: pf.contact_name || '', email: pf.contact_email || '', contact_no: pf.contact_phone || '' },
      sgsecure: prior.sgsecure || blankContact(),
      senior_mgmt: prior.senior_mgmt || blankContact(),
      senior_consent: !!prior.senior_consent,
    })
    setLoading(false)
    // Checkpoint 6: decide LEGACY vs NEW from the snapshot's OWN package_status (returned inside d.snapshot via
    // to_jsonb) — the authoritative signal, independent of the edge function.
    //   • package_status 'none' (or absent) → genuinely LEGACY / kill-switch job with no official package
    //     expected → LEGACY raster flow (old links keep working).
    //   • any other value (ready/preparing/error) → this is a NEW official-package job: REQUIRE the stored
    //     package via the token-scoped edge function. It NEVER falls back to legacy raster — if the package
    //     can't be streamed (not ready, or the edge function is unreachable), the client is shown "not ready"
    //     and endorsement is blocked, so no one can acknowledge without the real official package.
    const expected = d?.snapshot?.package_status || 'none'
    let pkgMode = 'legacy', pkgUrl = null
    if (expected !== 'none') {
      try {
        const { data: pk } = await supabase.functions.invoke('report-package', { body: { token } })
        if (pk?.ok && pk.url) { pkgMode = 'ready'; pkgUrl = pk.url }
        else if (pk?.status === 'expired') pkgMode = 'expired'   // new WSHC-portal jobs: 90 days after finishing
        else if (pk?.status === 'error') pkgMode = 'error'
        else pkgMode = 'preparing'   // preparing OR any not-ready/none from the fn → not ready (never legacy)
      } catch (e) { pkgMode = 'unavailable' }   // edge fn unreachable for a NEW job → block, do NOT bypass
    }
    setPkg({ mode: pkgMode, url: pkgUrl })
    if (pkgMode !== 'legacy') { setMediaReady(true); return }

    // ── Legacy raster flow (UNCHANGED) — only runs when there is no stored package ──
    // The audit-evidence bucket is PRIVATE, so this anon page cannot sign URLs client-side. Resolve every
    // evidence URL through the token-verified report-ack-media edge function (service-role signing) instead.
    const signer = async (p) => {
      try { const { data } = await supabase.functions.invoke('report-ack-media', { body: { token, path: p } }); return data?.url || null } catch (e) { return null }
    }
    const m = await resolveSnapshotMedia((d.snapshot?.snapshot?.evidence) || [], 3600, signer)
    setMedia(m); setMediaReady(true)
    const appxList = buildAppendixList((d.snapshot?.snapshot?.evidence) || [])
    setAppendices(appxList.map(a => ({ label: a.label, name: a.ev.file_name || 'attachment', url: m[a.ev.storage_path] || '', status: 'loading', pages: [] })))
    for (let i = 0; i < appxList.length; i++) {
      const url = m[appxList[i].ev.storage_path]
      let pages = [], status = 'error'
      if (url) { try { pages = await rasterisePdf(url); status = 'ready' } catch (e) {} }
      setAppendices(prev => prev.map((p, idx) => idx === i ? { ...p, url, pages, status } : p))
    }
  }

  const currentVersion = data?.current_version ?? null
  const ack = data?.ack || null
  const ackValid = ack && currentVersion != null && ack.report_version === currentVersion
  // A NEW official-package job whose package can't be shown yet — endorsement is blocked (never legacy-bypassed).
  const pkgBlocked = pkg.mode === 'preparing' || pkg.mode === 'error' || pkg.mode === 'unavailable'
  // New WSHC client-portal process
  const w = data?.wshc || {}
  const portal = w.flow === 'client_portal' && data?.job?.audit_type === 'bizSAFE Risk Management Audit'
  const windowOpen = !w.window_opens || todaySg() >= w.window_opens        // no current expiry = new applicant = apply now
  const linkExpired = portal && (pkg.mode === 'expired' || (w.link_expires_at && new Date(w.link_expires_at) < new Date()))
  const applicantOk = !!applicant.name.trim() && validEmail(applicant.email)

  // Download link created on click (a page left open would otherwise hold an expired 30-minute link).
  async function downloadReport() {
    setDownloading(true)
    try {
      const { data: pk } = await supabase.functions.invoke('report-package', { body: { token, download: true } })
      if (pk?.ok && pk.url) window.location.href = pk.url
      else toast(pk?.status === 'expired' ? 'This link has expired' : 'Your report is being updated', 'info', { detail: pk?.status === 'expired' ? 'Please contact us for a copy' : 'Please refresh this page shortly' })
    } catch (e) { failed("We couldn't download the report", e.message, 'Please try again') }
    setDownloading(false)
  }

  // "Yes, we've submitted" (from the reminder emails). Nothing is recorded by just opening the link.
  async function markApplied() {
    const group = (w.group || []).filter(g => g.eligible && !g.submitted_at)
    const ids = (w.group || []).length ? group.filter(g => applyPicks[g.job_id]).map(g => g.job_id) : null
    if ((w.group || []).length && !ids.length) { toast('Tick the companies you have applied for', 'warning'); return }
    setApplyBusy(true)
    const { data: res, error } = await supabase.rpc('mark_client_wshc_submitted', { p_token: token, p_job_ids: ids })
    setApplyBusy(false)
    if (error) { failed("We couldn't record that", error.message, 'Please try again'); return }
    if (!res?.recorded) { toast('Nothing was recorded', 'warning', { detail: 'Please reply to our email or call us at (65) 6376 0550' }); return }
    toast('Thank you — recorded', 'success', { detail: 'We will stop the reminders for the companies you ticked' })
    await load()
  }

  const needEnterprise = ['Renewal', 'Progression'].includes(app?.application_type)
  const appComplete = !!app && !!app.company_type && !!app.is_sme && (app.has_certs?.length > 0) && !!app.industry_sector && !!app.level
    && (!needEnterprise || !!app.enterprise_no?.trim()) && !!app.company_email?.trim() && !!(app.business_description || '').trim()
    && contactComplete(app.contact) && contactComplete(app.sgsecure) && contactComplete(app.senior_mgmt) && !!app.senior_consent

  const toggleCert = (opt) => setApp(a => {
    let certs = a.has_certs || []
    if (opt === NONE_CERT) certs = certs.includes(NONE_CERT) ? [] : [NONE_CERT]
    else certs = certs.includes(opt) ? certs.filter(x => x !== opt) : [...certs.filter(x => x !== NONE_CERT), opt]
    return { ...a, has_certs: certs }
  })
  const fErr = (bad) => showErrors && bad ? { borderColor: '#E24B4A' } : {}

  // "Save details for later" — persists the bizSAFE application form to the job WITHOUT signing. Lets the
  // client's ADMIN fill everything in and save, so TOP MANAGEMENT can reopen the SAME link and just endorse
  // (the saved details are restored automatically on reload). Writes only jobs.bizsafe_application — records
  // no acknowledgement. Partial saves are allowed (no required-field gating here).
  async function saveDraft() {
    if (underRevision || pkg.mode === 'preparing' || pkg.mode === 'error' || pkg.mode === 'unavailable') return
    setSavingDraft(true)
    const { error } = await supabase.rpc('save_report_ack_draft', { p_token: token, p_application: app })
    setSavingDraft(false)
    if (error) { failed("We couldn't save your details", error.message, 'Please try again'); return }
    setDraftSaved(true)
    toast('Details saved', 'success', { detail: 'Tick the confirmation and sign at the bottom to finish' })
  }

  async function submit() {
    if (underRevision) { toast('This report is being corrected', 'info', { detail: "We'll send you the updated version" }); return }
    if (pkg.mode === 'preparing' || pkg.mode === 'error' || pkg.mode === 'unavailable') { toast('Your report is not ready yet', 'info', { detail: 'Please try again in a little while' }); return }
    const ackOk = form.confirm && form.name.trim() && form.designation.trim()
    if (!ackOk || !(portal ? applicantOk : appComplete)) {
      setShowErrors(true)
      toast('Please complete the highlighted fields', 'warning', { detail: 'Including the confirmation tick and signature' })
      return
    }
    setSubmitting(true)
    // New WSHC-portal process: save who will apply on WSHC first (they receive the steps by email).
    if (portal) {
      const { error: aErr } = await supabase.rpc('set_wshc_applicant', { p_token: token, p_name: applicant.name, p_email: applicant.email })
      if (aErr) { setSubmitting(false); failed("We couldn't save your assigned staff's details", aErr.message, 'Please check and try again'); return }
    }
    let ip = 'unknown'
    try { const r = await fetch('https://api.ipify.org?format=json'); ip = (await r.json()).ip } catch (e) {}
    const { error } = await supabase.rpc('submit_report_ack', {
      p_token: token, p_name: form.name, p_designation: form.designation, p_company: data.client?.name || null,
      // Always passed (null on the new process) — keeps the single live submit_report_ack signature.
      p_ip: ip, p_ua: navigator.userAgent, p_application: portal ? null : app,
    })
    setSubmitting(false)
    if (error) { failed("We couldn't submit your acknowledgement", error.message, 'Please try again'); return }
    // The stored package was rendered BEFORE this signature, so it is the UNSIGNED copy. Re-render it now
    // (authorised by this page's own link token) so the office and WSHC get the SIGNED report. Best-effort: the
    // office side also self-heals, and WSHC submission stays blocked until the signed copy exists.
    try {
      await fetch(`${window.location.origin}/.netlify/functions/render-report-package-background`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ackToken: token, siteOverride: window.location.origin }),
      })
    } catch (e) { /* non-blocking */ }
    // Best-effort: notify the routed office handler that the client has signed, so they can submit to WSHC.
    // If no handler was routed (the Decision-Maker kept it), no email is sent. Never blocks the client.
    // New WSHC-portal process: the office is told in the daily bizSAFE@ summary instead (nothing to submit).
    if (data.job?.assigned_to_id && !portal) {
      try {
        await supabase.functions.invoke('send-internal-email', {
          body: {
            user_id: data.job.assigned_to_id,
            subject: `Audit report signed – ${data.client?.name || 'client'}`,
            html: emailHtml({
              title: 'Audit report signed by client',
              greeting: `Dear ${data.job.assigned_to_name || 'colleague'},`,
              lines: [`${data.client?.name || 'The client'} has signed the audit report. Please submit the online application to WSHC and record the submission in AuditPM.`],
              details: [['Company', data.client?.name || '—']],
              button: { label: 'Open job →', url: `${window.location.origin}/jobs/${data.job.id}` },
            }),
          },
        })
      } catch (e) { /* best-effort — non-blocking */ }
    }
    await load()
  }

  if (loading) return <div style={{ ...wrap, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ color: '#999' }}>Loading…</span></div>
  if (invalid) return (
    <div style={{ ...wrap, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ ...sheet, maxWidth: 440, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔗</div>
        <h2 style={{ fontWeight: 500, marginBottom: 8 }}>Invalid link</h2>
        <p style={{ color: '#666', fontSize: 14 }}>This acknowledgement link is not valid. Please contact your audit coordinator.</p>
      </div>
    </div>
  )

  const doc = { client: data.client, job: data.job, snapshot: data.snapshot, ack, currentVersion, checklistRevision: data.checklist_revision }

  // New WSHC-portal process: the link stops working 90 days after the job is finished.
  if (linkExpired) return (
    <div style={{ ...wrap, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ ...sheet, maxWidth: 480, textAlign: 'center' }}>
        <h2 style={{ fontWeight: 500, marginBottom: 8 }}>This link has expired</h2>
        <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6 }}>Report links stop working 90 days after the certificate is issued. Need a copy? Call us at (65) 6376 0550.</p>
      </div>
    </div>
  )

  const keepCopy = <div style={{ marginTop: 10, fontSize: 13 }}><strong>Save a copy of your report now.</strong> This link stops working 90 days after your certificate is issued.</div>
  // "Already applied" card: shown whenever the apply steps have been emailed and the job is still open (the
  // applicant also reaches this page from the Download link). Opening it records nothing — they confirm here.
  // Combined audit: one tick per company that can say "applied" now.
  const appliedCard = portal && w.apply_email_sent_at && !w.finished && (() => {
    const group = (w.group || []).filter(g => g.eligible || g.submitted_at)
    const pending = (w.group || []).length ? group.filter(g => !g.submitted_at) : (w.client_submitted_at ? [] : [{ job_id: data.job.id }])
    if (!pending.length) return askApplied ? (
      <div style={{ marginBottom: 18, padding: '12px 16px', background: '#EAF3DE', borderRadius: 8, border: '0.5px solid #639922', color: '#27500a', fontSize: 14 }}>
        ✓ Thanks. Reminders stopped. WSHC will email your company the result.
      </div>
    ) : null
    return (
      <div style={{ marginBottom: 18, padding: '14px 16px', background: '#F3F5F9', borderRadius: 8, border: '2px solid #006363', fontSize: 14 }}>
        <div style={{ fontWeight: 600, fontSize: 16 }}>Already submitted your application with WSHC?</div>
        <div style={{ margin: '4px 0 10px' }}>Click the following button to stop our reminders.</div>
        {group.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '0 0 10px' }}>
            {group.map(g => (
              <label key={g.job_id} style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: g.submitted_at ? 'default' : 'pointer' }}>
                <input type="checkbox" disabled={!!g.submitted_at} checked={!!g.submitted_at || !!applyPicks[g.job_id]} onChange={e => setApplyPicks(p => ({ ...p, [g.job_id]: e.target.checked }))} />
                <span>{g.company}{g.uen ? ` (UEN ${g.uen})` : ''}{g.submitted_at ? ' — already told us' : ''}</span>
              </label>
            ))}
          </div>
        )}
        <button className="primary" disabled={applyBusy} onClick={markApplied}>{applyBusy ? 'Recording…' : 'Yes, we’ve applied'}</button>
      </div>
    )
  })()

  // After signing (new process): one title + one "Next:" line, then the deadline and the save-a-copy line.
  // Wording works for both readers: the signer, and the applicant who lands here from the Download link.
  const applicantLabel = w.applicant_name || w.applicant_email || 'your assigned staff'
  const deadline = w.expiry ? `Deadline: apply before your bizSAFE expires on ${fmtDate(w.expiry)}.` : null
  const signedNext = w.client_submitted_at
    ? { title: 'You’ve told us you applied. Thank you.', next: 'WSHC will email your company the result, usually within 1 to 10 working days. Nothing more to do.' }
    : !windowOpen
      ? { title: 'Report acknowledged. Thank you.', next: `we will email ${applicantLabel} the application steps on ${fmtDate(w.window_opens)}, when the renewal window opens. They must apply before your bizSAFE expires on ${fmtDate(w.expiry)}.` }
      : w.apply_email_sent_at
        ? { title: 'Report acknowledged.', next: 'apply for bizSAFE with WSHC using the steps below.', steps: true, extra: deadline }
        : { title: 'Report acknowledged. Thank you.', next: `we will email ${applicantLabel} the bizSAFE application steps.`, extra: deadline }

  return (
    <div style={wrap}>
      <div style={sheet}>
        {portal && ackValid && !underRevision ? (
          <div style={{ marginBottom: 18, padding: '14px 16px', background: '#EAF3DE', borderRadius: 8, border: '0.5px solid #639922', color: '#1f3d08', fontSize: 14, lineHeight: 1.6 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>✓ {w.finished ? 'Report acknowledged. Thank you.' : signedNext.title}</div>
            {!w.finished && <div style={{ marginTop: 6 }}><strong>Next:</strong> {signedNext.next}</div>}
            {!w.finished && signedNext.extra && <div style={{ marginTop: 4, fontWeight: 600 }}>{signedNext.extra}</div>}
            {!w.finished && signedNext.steps && <ApplySteps />}
            {keepCopy}
            {pkgBlocked && <div style={{ marginTop: 6, fontSize: 13, color: '#7a5b00' }}>Your signed copy is being prepared — please refresh this page in a few minutes to download it.</div>}
            <div style={{ marginTop: 8, fontSize: 12, color: '#56703f' }}>Signed version {ack.report_version} on {new Date(ack.submitted_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}.</div>
          </div>
        ) : null}
        {appliedCard}
        {portal && ackValid && !underRevision ? null : underRevision ? (
          <div style={{ marginBottom: 18, padding: '14px 16px', background: '#FFF3E0', borderRadius: 8, border: '1px solid #E6A817', color: '#7a5b00', fontSize: 14 }}>
            <strong>This report has been withdrawn for revision.</strong> A corrected version will be re-issued for your acknowledgement. No action is needed right now.
          </div>
        ) : ackValid ? (
          <div style={{ marginBottom: 18, padding: '12px 16px', background: '#EAF3DE', borderRadius: 8, border: '0.5px solid #639922', color: '#27500a' }}>
            ✓ Thank you — your acknowledgement of this report (version {ack.report_version}) has been recorded on {new Date(ack.submitted_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}. No further action is needed.
          </div>
        ) : pkgBlocked ? (
          <div style={{ marginBottom: 18, padding: '14px 16px', background: '#FFF3E0', borderRadius: 8, border: '1px solid #E6A817', color: '#7a5b00', fontSize: 14 }}>
            {pkg.mode === 'preparing'
              ? <><strong>Your report is being prepared.</strong> Please refresh this page in a few minutes to review and endorse it.</>
              : <><strong>Your report isn’t available just yet.</strong> Please refresh this page shortly, or contact your audit coordinator.</>}
          </div>
        ) : portal && w.finished ? (
          <div style={{ marginBottom: 18, padding: '14px 16px', background: '#f5f5f5', borderRadius: 8, border: '1px solid #ccc', color: '#444', fontSize: 14 }}>
            This bizSAFE job is closed.{w.link_expires_at ? ` You can download your report until ${fmtDate(w.link_expires_at)}. Please save a copy now.` : ''} Questions? Call us at (65) 6376 0550.
          </div>
        ) : portal ? (
          <div style={{ marginBottom: 18, padding: '14px 16px', background: '#FDECEA', borderRadius: 8, border: '1px solid #C0392B', color: '#791F1F', fontSize: 14, lineHeight: 1.6 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Action required: check and acknowledge your report</div>
            <div style={{ marginTop: 6 }}>{PORTAL_LEAD} Please check the report, then scroll to the bottom: enter the staff who will apply for bizSAFE for your company, tick the confirmation box and click “Submit acknowledgement”.</div>
            {ack && <div style={{ marginTop: 6 }}>This report has been revised since your last acknowledgement — please re-acknowledge the current version.</div>}
            <div style={{ marginTop: 12 }}>
              <button onClick={() => (applicantRef.current || endorseRef.current)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                style={{ background: '#C0392B', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>
                Go to Submit acknowledgement ↓
              </button>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 18, padding: '14px 16px', background: '#FDECEA', borderRadius: 8, border: '1px solid #C0392B', color: '#791F1F', fontSize: 14 }}>
            <strong>Action required:</strong> Please review the audit report below, complete your bizSAFE application details, then <strong>scroll to the bottom of this page and sign to endorse the report.</strong> Your endorsement is required before we can proceed with your bizSAFE application.
            {ack && <div style={{ marginTop: 6 }}>This report has been revised since your last acknowledgement — please re-acknowledge the current version.</div>}
            <div style={{ marginTop: 12 }}>
              <button onClick={() => endorseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                style={{ background: '#C0392B', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>
                Sign / endorse ↓
              </button>
            </div>
          </div>
        )}

        {/* Checkpoint 6: STORED official package (one complete PDF) is streamed via an iframe when ready. When
            preparing/error the banner above carries the message (nothing to review yet). Legacy jobs (no stored
            package) fall back to the ORIGINAL inline ReportDocument + rasterised appendices — unchanged. */}
        {pkg.mode === 'ready' ? (
          <>
            <div style={{ textAlign: 'right', marginBottom: 10 }}>
              {portal
                ? <button className="sm" disabled={downloading} onClick={downloadReport}>{downloading ? 'Preparing…' : '⤓ Download report (PDF)'}</button>
                : <a href={pkg.url} target="_blank" rel="noreferrer"><button className="sm">⤓ Download report (PDF)</button></a>}
            </div>
            <iframe title="Audit report" src={pkg.url} style={{ width: '100%', height: '75vh', border: '0.5px solid #ddd', borderRadius: 8, background: '#fff' }} />
            <div style={{ fontSize: 12, color: '#777', marginTop: 8 }}>This is your complete official report package — the audit report and all appendices. Please review it above, then complete the details and endorse below.</div>
          </>
        ) : pkgBlocked ? null : (
          <>
            <div style={{ textAlign: 'right', marginBottom: 10 }}>
              {!mediaReady && <span style={{ fontSize: 11, color: '#888', marginRight: 8 }}>Loading photos…</span>}
              <button className="sm" onClick={printReport} disabled={!mediaReady}>Print / Save as PDF</button>
            </div>
            <div ref={reportRef}>
              <ReportDocument doc={doc} logos={data.logos || {}} media={media} onDownload={(ev) => { const u = media[ev?.storage_path]; if (u) window.open(u, '_blank') }} />
            </div>
            {appendices.map((a, i) => (
              <div key={i} style={{ marginTop: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>{a.label}</div>
                {a.status === 'loading' && <div style={{ fontSize: 12, color: '#999' }}>Preparing attachment…</div>}
                {a.status === 'ready' && a.pages.map((p, j) => <img key={j} src={p.screen} alt={`${a.label} page ${j + 1}`} style={{ width: '100%', display: 'block', marginBottom: 10, border: '0.5px solid #ddd' }} />)}
                {a.status === 'error' && (
                  <div style={{ fontSize: 12.5, color: '#555' }}>
                    {a.url
                      ? <>This attachment <strong>forms part of your audit report package</strong>. <a href={a.url} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>View attachment →</a></>
                      : 'This attachment could not be displayed.'}
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {/* New WSHC-portal process: instead of the application form, just WHO will apply on WSHC's portal. */}
        {portal && !w.finished && !ackValid && !underRevision && !pkgBlocked && (
          <div ref={applicantRef} style={{ marginTop: 24, borderTop: '2px solid #333', paddingTop: 18, scrollMarginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Staff who will apply for bizSAFE for your company</div>
            <div style={{ fontSize: 12.5, color: '#666', marginBottom: 12, lineHeight: 1.5 }}>
              {windowOpen
                ? 'He/she will need your company’s CorpPass (it can be you). We will email your assigned staff the bizSAFE application steps.'
                : `He/she will need your company’s CorpPass (it can be you). We will email your assigned staff the application steps on ${fmtDate(w.window_opens)}, when the renewal window opens.`}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
              <div className="field"><label>Staff name *</label>
                <input value={applicant.name} onChange={e => setApplicant(a => ({ ...a, name: e.target.value }))} style={fErr(!applicant.name.trim())} /></div>
              <div className="field"><label>Staff email *</label>
                <input type="email" value={applicant.email} onChange={e => setApplicant(a => ({ ...a, email: e.target.value }))} style={fErr(!validEmail(applicant.email))} placeholder="name@company.com" /></div>
            </div>
          </div>
        )}

        {!portal && !ackValid && !underRevision && !pkgBlocked && app && (
          <div style={{ marginTop: 24, borderTop: '2px solid #333', paddingTop: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>bizSAFE Application Details</div>
            <div style={{ fontSize: 12, color: '#777', marginBottom: 14 }}>We need these to submit your bizSAFE application to WSHC. Greyed fields are taken from your audit/application — please contact us if anything is incorrect.</div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
              <div className="field"><label>Company Name (as per UEN)</label><input value={app.company_name} readOnly style={lockStyle} /></div>
              <div className="field"><label>Unique Entity Number (UEN)</label><input value={app.uen} readOnly style={lockStyle} /></div>
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>Are you using a shared UEN number?</label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, cursor: 'pointer', marginTop: 4 }}>
                  <input type="checkbox" checked={app.subsidiary} onChange={e => setApp(a => ({ ...a, subsidiary: e.target.checked }))} style={{ marginTop: 3 }} />
                  <span>Tick if Yes — <strong>for subsidiary companies only</strong>. Do not tick if your company is an Approved Scaffold Contractor (ASC) and/or Approved Crane Contractor (ACC).</span>
                </label></div>
              <div className="field"><label>Application Type</label><input value={app.application_type} readOnly style={lockStyle} /></div>
              <div className="field"><label>Company Type *</label>
                <select value={app.company_type} onChange={e => setApp(a => ({ ...a, company_type: e.target.value }))} style={fErr(!app.company_type)}>
                  <option value="">Select…</option>{COMPANY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select></div>
              <div className="field" style={{ gridColumn: '1 / -1', ...(showErrors && !app.is_sme ? { outline: '1px solid #E24B4A', borderRadius: 6, padding: 6 } : {}) }}>
                <label>Is your company an SME? *</label>
                <div style={{ display: 'flex', gap: 24, marginTop: 4 }}>
                  {['Yes', 'No'].map(opt => (
                    <label key={opt} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
                      <input type="radio" name="is_sme" checked={app.is_sme === opt} onChange={() => setApp(a => ({ ...a, is_sme: opt }))} />{opt}
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: 11.5, color: '#777', marginTop: 6, fontStyle: 'italic', lineHeight: 1.5 }}>
                  Small Medium Enterprises (SME) in Singapore are defined as companies with at least 30% local shareholding; Group annual sales turnover of less than $100 million; or Group employment size of not more than 200 workers.
                </div>
              </div>
              <div className="field"><label>Industry Sector *</label>
                <select value={app.industry_sector} onChange={e => setApp(a => ({ ...a, industry_sector: e.target.value }))} style={fErr(!app.industry_sector)}>
                  <option value="">Select…</option>{INDUSTRY_SECTORS.map(t => <option key={t} value={t}>{t}</option>)}
                </select></div>
              <div className="field"><label>bizSAFE Level *</label>
                <select value={app.level} onChange={e => setApp(a => ({ ...a, level: e.target.value }))} style={fErr(!app.level)}>
                  <option value="">Select…</option>{LEVELS.map(t => <option key={t} value={t}>{t}</option>)}
                </select></div>
              {needEnterprise && <div className="field"><label>bizSAFE Enterprise No. (starts with “E”) *</label><input value={app.enterprise_no} onChange={e => setApp(a => ({ ...a, enterprise_no: e.target.value }))} style={fErr(!app.enterprise_no?.trim())} /></div>}
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>Address</label><input value={app.address} readOnly style={lockStyle} /></div>
              <div className="field"><label>Total No. of Employees</label><input value={app.employees} readOnly style={lockStyle} title="Verified during the audit — matches the report" /></div>
              <div className="field"><label>Company Email *</label><input value={app.company_email} onChange={e => setApp(a => ({ ...a, company_email: e.target.value }))} style={fErr(!app.company_email?.trim())} /></div>
              <div className="field"><label>Company Website</label><input value={app.company_website} onChange={e => setApp(a => ({ ...a, company_website: e.target.value }))} /></div>
            </div>

            <div className="field" style={{ marginTop: 10 }}><label>Company Business Description *</label>
              <div style={{ fontSize: 11.5, color: '#777', marginBottom: 4 }}>Describe your business within 50 words — what you offer / sell.</div>
              <textarea rows={3} value={app.business_description} onChange={e => setApp(a => ({ ...a, business_description: e.target.value }))} style={fErr(!(app.business_description || '').trim())} /></div>

            <div className="field" style={{ marginTop: 10 }}><label>Key Search Words</label>
              <div style={{ fontSize: 11.5, color: '#777', marginBottom: 4 }}>Within 50 words, e.g. Air-conditioner, Flooring, Metal working, Printing, Transport services…</div>
              <textarea rows={2} value={app.key_search_words} onChange={e => setApp(a => ({ ...a, key_search_words: e.target.value }))} /></div>

            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Do you have any of the following? *</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...(showErrors && !(app.has_certs?.length) ? { outline: '1px solid #E24B4A', borderRadius: 6, padding: 6 } : {}) }}>
                {CERT_OPTIONS.map(opt => (
                  <label key={opt} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={app.has_certs?.includes(opt) || false} onChange={() => toggleCert(opt)} />{opt}
                  </label>
                ))}
              </div>
            </div>

            <ContactBlock title="Contact Person Details" value={app.contact} onChange={v => setApp(a => ({ ...a, contact: v }))} invalid={showErrors} />
            <ContactBlock title="SGSecure Representative Details" value={app.sgsecure} onChange={v => setApp(a => ({ ...a, sgsecure: v }))} onCopy={() => setApp(a => ({ ...a, sgsecure: { ...a.contact } }))} invalid={showErrors} />

            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>Senior Management Details</div>
                <button type="button" className="sm" onClick={() => setApp(a => ({ ...a, senior_mgmt: { ...a.contact } }))}>Copy from Contact Person</button>
              </div>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '8px 0 10px', fontSize: 13, cursor: 'pointer', ...(showErrors && !app.senior_consent ? { color: '#791F1F' } : {}) }}>
                <input type="checkbox" checked={app.senior_consent} onChange={e => setApp(a => ({ ...a, senior_consent: e.target.checked }))} style={{ marginTop: 3 }} />
                <span>I understand that the approval email from WSHC containing the E-Certificate will only be sent to the Senior Management listed below.</span>
              </label>
              <ContactBlock value={app.senior_mgmt} onChange={v => setApp(a => ({ ...a, senior_mgmt: v }))} invalid={showErrors} bare />
            </div>

            {/* Save-for-later: the admin can fill in and save these details WITHOUT signing, so top management
                can reopen this same link and just endorse below (details are restored automatically). */}
            <div style={{ marginTop: 18, padding: '12px 14px', background: '#F3F5F9', borderRadius: 8, border: '0.5px solid #d7dce6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <button type="button" disabled={savingDraft} onClick={saveDraft}
                  style={{ background: '#fff', border: '1px solid #4A6DA7', color: '#33507d', borderRadius: 8, padding: '9px 16px', fontSize: 13.5, fontWeight: 600, cursor: savingDraft ? 'default' : 'pointer' }}>
                  {savingDraft ? 'Saving…' : '💾 Save these details for later'}
                </button>
                <span style={{ fontSize: 12, color: '#5a6473', maxWidth: 520, lineHeight: 1.5 }}>
                  Filling this in on someone else’s behalf? Save now — no signature needed yet. When your
                  <strong> management reopens this same link</strong>, everything here will already be filled in
                  and they only need to sign below.
                </span>
              </div>
              {draftSaved && (
                <div style={{ fontSize: 12.5, color: '#27500a', marginTop: 10 }}>
                  ✓ Saved. These details will be here the next time this link is opened.
                </div>
              )}
            </div>
          </div>
        )}

        {!ackValid && !underRevision && !pkgBlocked && !(portal && w.finished) && (
          <div ref={endorseRef} style={{ marginTop: 24, borderTop: '2px solid #333', paddingTop: 18, scrollMarginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: portal ? 4 : 12 }}>Client Acknowledgement</div>
            {portal && <div style={{ fontSize: 12.5, color: '#666', marginBottom: 12 }}>Tick the box, enter your name and designation, then click “Submit acknowledgement”.</div>}
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', marginBottom: 16, fontSize: 13.5 }}>
              <input type="checkbox" checked={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.checked }))} style={{ marginTop: 3 }} />
              <span>{DECLARATION}</span>
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
              <div className="field"><label>Management Representative's Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={fErr(!form.name.trim())} /></div>
              <div className="field"><label>Management Representative's Designation *</label>
                <input value={form.designation} onChange={e => setForm(f => ({ ...f, designation: e.target.value }))} style={fErr(!form.designation.trim())} /></div>
            </div>
            <div style={{ fontSize: 11.5, color: '#777', margin: '4px 0 14px' }}>
              Your acknowledgement is recorded electronically with the date, time, your network address and the report version.
              {data.sent_to ? ` This secure link was sent to ${data.sent_to}.` : ''} The record cannot be edited afterwards.
            </div>
            <button className="primary" disabled={submitting} onClick={submit}>
              {submitting ? 'Submitting…' : 'Submit acknowledgement'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
